/**
 * Uniform helpers for surfacing server-side ApiError envelopes to the operator.
 *
 * The axios response interceptor rejects with `error.response.data`, so an
 * `unknown` caught in a mutation's `onError` is (in the vast majority of
 * cases) the shared `ApiError` shape: `{ statusCode, message, code, details, timestamp, path }`.
 *
 * `extractServerError` teases apart the different flavours of 4xx that the
 * API emits (Zod validation, business-rule failures, unique-constraint
 * violations) and returns:
 *   - `message`: the most specific human-readable string we can find
 *   - `field`:   the RHF field path (dot-separated) that caused it, when
 *                the server included one - so callers can scroll/focus
 *
 * `focusFormField(name)` scrolls to a form control by `[name=...]` and
 * focuses it - matches the client-side `scrollToFirstError` behaviour so
 * the operator experience is symmetric whether the failure came from
 * client Zod or the server.
 */

interface ZodIssue {
  code?: string;
  path?: Array<string | number>;
  message?: string;
}

interface MissingField {
  field?: string;
  label?: string;
}

interface ApiErrorLike {
  statusCode?: number;
  message?: unknown;
  code?: string;
  details?: {
    message?: unknown;
    issues?: ZodIssue[];
    missing?: MissingField[];
    // pass-through - some endpoints attach the field directly
    field?: string;
    /**
     * 2026-07-20. On 5xx responses the exception filter may attach debug
     * details (dev only): `originalMessage` is the raw exception message.
     * Toasts must NOT show Prisma/SQL dumps — see `sanitizeUserFacingError`.
     */
    originalMessage?: unknown;
    [k: string]: unknown;
  };
}

export interface ExtractedServerError {
  /** Best-effort human message to put in the toaster. */
  message: string | null;
  /** RHF field path (`"expectedTravelStart"`, `"customer.email"`) when the server pinpointed one. */
  field: string | null;
  /** Machine code from the server (`validation_error`, `lead_not_active_ready`, ...). */
  code: string | null;
}

function issueToPath(issue: ZodIssue): string | null {
  if (!issue.path || issue.path.length === 0) return null;
  return issue.path.map((seg) => String(seg)).join('.');
}

export function extractServerError(err: unknown): ExtractedServerError {
  if (!err || typeof err !== 'object') {
    return { message: null, field: null, code: null };
  }
  if (err instanceof Error && !(err as Error & ApiErrorLike).details) {
    return { message: sanitizeUserFacingError(err.message), field: null, code: null };
  }
  const e = err as ApiErrorLike;
  const code = typeof e.code === 'string' ? e.code : null;

  // 1. Zod validation error - take the first issue.
  if (code === 'validation_error') {
    const first = e.details?.issues?.[0];
    if (first) {
      const path = issueToPath(first);
      const msg = first.message ?? 'Invalid value';
      return { message: path ? `${path}: ${msg}` : msg, field: path, code };
    }
  }

  // 2. Business "not ready" errors (readiness, permission, etc.) - the
  //    server hangs a specific `missing[]` list off `details`. First
  //    label wins for the toaster; first field wins for focus.
  const missing = e.details?.missing;
  if (Array.isArray(missing) && missing.length > 0) {
    const labels = missing.map((m) => m?.label).filter((s): s is string => Boolean(s));
    const fields = missing.map((m) => m?.field).filter((s): s is string => Boolean(s));
    const first = fields[0] ?? null;
    const detailMsg =
      typeof e.details?.message === 'string'
        ? e.details.message
        : typeof e.message === 'string'
          ? e.message
          : null;
    return {
      message: labels.length > 0 ? `Missing: ${labels.join(', ')}` : detailMsg,
      field: first,
      code,
    };
  }

  // 3. Explicit field hint on details.
  const explicitField = typeof e.details?.field === 'string' ? e.details.field : null;

  // 4. Fall back to the most-specific message we can find.
  //    Prefer a human-readable API message. Never surface raw Prisma / SQL /
  //    stack dumps in the toast — those stay in the network tab for engineers.
  //    `details.originalMessage` is debug-only and must never drive the toast.
  const rawTop = typeof e.message === 'string' ? e.message : null;
  const rawDetail = typeof e.details?.message === 'string' ? e.details.message : null;
  const candidates = [rawDetail, rawTop];
  const picked =
    candidates.find((m): m is string => typeof m === 'string' && m.trim().length > 0) ?? null;
  const detailMsg = sanitizeUserFacingError(picked);

  return { message: detailMsg, field: explicitField, code };
}

/** True when a message looks like a Prisma / SQL / driver dump. */
function isTechnicalDbError(message: string): boolean {
  return (
    /PrismaClient/i.test(message) ||
    /Invalid `prisma\./i.test(message) ||
    /does not exist in the current database/i.test(message) ||
    /column .+ does not exist/i.test(message) ||
    /relation .+ does not exist/i.test(message) ||
    /Unique constraint failed/i.test(message) ||
    /Foreign key constraint/i.test(message) ||
    /P20\d{2}/.test(message) ||
    /DriverAdapterError/i.test(message) ||
    /at zr\.handleRequestError/i.test(message)
  );
}

/**
 * Map technical backend failures to short operator-facing copy. Keep genuine
 * business messages (validation, "not found", PayU, TBO) intact.
 */
function sanitizeUserFacingError(message: string | null): string | null {
  if (!message) return null;
  const trimmed = message.trim();
  if (!trimmed || trimmed === 'Internal server error') {
    return 'Something went wrong. Please try again.';
  }
  if (isTechnicalDbError(trimmed)) {
    return 'Something went wrong while saving. Please try again. If this keeps happening, contact support.';
  }
  // Nested "Could not verify…: <prisma dump>"
  const nested = /^(.+?):\s*(Invalid `prisma\.|PrismaClient|column .+ does not exist)/i.exec(
    trimmed,
  );
  if (nested?.[1]) {
    return `${nested[1].trim()}. Please try again.`;
  }
  return trimmed;
}

/**
 * Scroll a form control named `fieldName` into view and focus it. Native
 * inputs (`<input name="...">`) work out of the box; the custom Controlled
 * / DateControlled / NumberControlled wrappers all render a child element
 * with a matching `name` attribute so the same query works there.
 *
 * Deferred to the next tick so any just-triggered React re-render has a
 * chance to mount the element first.
 */
export function focusFormField(fieldName: string | null | undefined): void {
  if (!fieldName) return;
  const safeName = fieldName.replace(/(["\\])/g, '\\$1');
  setTimeout(() => {
    const el = document.querySelector<HTMLElement>(`[name="${safeName}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof (el as HTMLInputElement).focus === 'function') {
      (el as HTMLInputElement).focus({ preventScroll: true });
    }
  }, 0);
}
